import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button } from 'antd';
import { FileTextOutlined, LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, TeamOutlined, MenuOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import '../styles/MobileMenuShared.css';

const { Sider } = Layout;

/**
 * AdminSidebar Component
 * 
 * Provides navigation sidebar for admin pages
 * @param {Object} props - Component props
 * @param {string} props.activePage - Currently active page
 * @param {Function} props.onCollapse - Callback when sidebar is collapsed/expanded
 * @returns {JSX.Element} The AdminSidebar component
 */
const AdminSidebar = ({ activePage, onCollapse }) => {
  const history = useHistory();
  // Get initial collapsed state from localStorage or default to false (expanded)
  const [collapsed, setCollapsed] = useState(() => {
    const savedState = localStorage.getItem('admin_sidebar_collapsed');
    return savedState ? JSON.parse(savedState) : false;
  });

  // State for mobile sidebar visibility
  const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);

  // Save collapsed state to localStorage and notify parent when changes
  useEffect(() => {
    localStorage.setItem('admin_sidebar_collapsed', JSON.stringify(collapsed));
    if (onCollapse) {
      onCollapse(collapsed);
    }
  }, [collapsed, onCollapse]);

  const handleNavigation = (path) => {
    // Navigate without collapsing the sidebar
    history.push(path);
    // Close mobile sidebar if open
    setMobileSidebarVisible(false);
  };

  // Toggle mobile sidebar
  const toggleMobileSidebarVisibility = () => {
    setMobileSidebarVisible(!mobileSidebarVisible);
  };

  const menuItems = [
    {
      key: 'acknowledgments',
      icon: <FileTextOutlined />,
      label: 'Receipts',
      onClick: () => handleNavigation('/admin-acknowledgment-receipt')
    },
    {
      key: 'scholars',
      icon: <TeamOutlined />,
      label: 'Scholars',
      onClick: () => handleNavigation('/admin-scholar-management')
    },
    {
      key: 'reports',
      icon: <FileTextOutlined />,
      label: 'Report',
      onClick: () => handleNavigation('/admin-report')
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: () => handleNavigation('/admin-logout')
    }
  ];

  return (
    <>
      {/* Mobile Menu Toggle Button */}
      <Button
        type="primary"
        className="mobile-sidebar-toggle menu-btn"
        icon={<MenuOutlined />}
        onClick={toggleMobileSidebarVisibility}
      />

      {/* Mobile Menu Overlay */}
      <div 
        className={`mobile-menu-overlay ${mobileSidebarVisible ? 'visible' : ''}`}
        onClick={() => setMobileSidebarVisible(false)}
      />

      {/* Mobile Menu Popup */}
      <div className={`mobile-menu-popup ${mobileSidebarVisible ? 'visible' : ''}`}>
        {menuItems.map(item => (
          <div
            key={item.key}
            className={`mobile-menu-item ${activePage === item.key ? 'active' : ''}`}
            onClick={() => item.onClick()}
          >
            {item.icon}
            <span>{item.label}</span>
          </div>
        ))}
      </div>

      {/* Desktop Sidebar */}
      <Sider
        className="admin-sidebar-component"
        theme="light"
        width={250}
        collapsedWidth={80}
        collapsed={collapsed}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0
        }}
        // Prevent auto-collapsing by setting breakpoint to a very low value
        breakpoint="xs"
        // Keep the current state when window resizes
        onBreakpoint={(broken) => {
          // Only respond to breakpoint if manually set
          if (broken) {
            // Save the current state before mobile view forces collapse
            localStorage.setItem('admin_sidebar_collapsed_before_mobile', JSON.stringify(collapsed));
          } else {
            // Restore previous state when returning from mobile view
            const previousState = localStorage.getItem('admin_sidebar_collapsed_before_mobile');
            if (previousState !== null) {
              setCollapsed(JSON.parse(previousState));
            }
          }
        }}
      >
        <div style={{ height: '64px', padding: '16px', textAlign: 'center' }}>
          <h2 style={{ margin: 0 }}>{collapsed ? 'AP' : 'Admin Panel'}</h2>
        </div>
        <Menu
          className="admin-sidebar-menu"
          mode="inline"
          selectedKeys={[activePage]}
          items={menuItems}
          style={{ borderRight: 0 }}
          // Prevent collapsing when clicking on menu items
          inlineCollapsed={collapsed}
        />
        <div className="admin-sidebar-trigger">
          {collapsed ? (
            <MenuUnfoldOutlined onClick={() => setCollapsed(false)} />
          ) : (
            <MenuFoldOutlined onClick={() => setCollapsed(true)} />
          )}
        </div>
      </Sider>
    </>
  );
};

export default AdminSidebar; 